import { useEffect, useRef, useState } from 'react';
import { useDebounced } from './useDebounced';
import type { ListState } from './useListState';

/**
 * A list's free-text box, bound to its `q` (in the URL for a routed list).
 *
 * The input is local so typing stays instant; the debounced value is written to the list
 * state, which resets the page. Two rules keep the two copies honest:
 *  - only a value the user TYPED is written back — a `q` that arrives from elsewhere (Back,
 *    a shared link, "Clear all") never bounces back and never resets the page;
 *  - a `q` that changes from elsewhere replaces what is in the box.
 */
export function useSearchInput(list: Pick<ListState, 'search' | 'setSearch'>, delay = 300) {
  const { search, setSearch } = list;
  const [value, setValue] = useState(search);
  const debounced = useDebounced(value, delay);

  const [seen, setSeen] = useState(search);
  if (seen !== search) {
    setSeen(search);
    if (search.trim() !== value.trim()) setValue(search);
  }

  const pushed = useRef(debounced);
  useEffect(() => {
    if (debounced === pushed.current) return;
    pushed.current = debounced;
    if (debounced.trim() === search.trim()) return;
    setSearch(debounced);
  }, [debounced, search, setSearch]);

  return [value, setValue] as const;
}
